import type { MarketingCampaign } from '@/api/marketing.api';
import { Button } from '@/components/ui/Button';
import { KeyValueTable } from '@/components/ui/KeyValueTable';
import { MarketingEmptyState } from './MarketingEmptyState';

type Perf = Record<string, number>;

type Props = {
  campaign: MarketingCampaign | null;
  performance: Perf | null;
  loading?: boolean;
  onLoadAttribution?: (campaignId: number) => void;
  attribution?: Record<string, unknown> | null;
};

function pct(n: number | undefined) {
  if (n == null || Number.isNaN(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

export function CampaignPerformancePanel({
  campaign,
  performance,
  loading,
  onLoadAttribution,
  attribution,
}: Props) {
  if (!campaign) {
    return (
      <div className="card h-full">
        <MarketingEmptyState
          title="No campaign selected"
          description="Select a campaign from the list to view delivery and engagement metrics."
        />
      </div>
    );
  }

  return (
    <div className="card p-4 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">{campaign.campaignName}</h3>
        <p className="text-xs text-slate-500 mt-0.5">{campaign.channel}</p>
      </div>

      {loading && <p className="text-sm text-slate-500">Loading metrics…</p>}

      {!loading && performance && (
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <Metric label="Sent" value={performance.sent} />
          <Metric label="Opens" value={performance.opens} />
          <Metric label="Clicks" value={performance.clicks} />
          <Metric label="Bounces" value={performance.bounces} />
          <Metric label="Unsubscribes" value={performance.unsubscribes} />
          <Metric label="Conversions" value={performance.conversions} />
          <Metric label="Open rate" value={pct(performance.openRate)} />
          <Metric label="Click rate" value={pct(performance.clickRate)} />
          <Metric label="ROI" value={`${Number(performance.roi ?? 0).toFixed(1)}%`} className="col-span-2" />
        </dl>
      )}

      {onLoadAttribution && (
        <div className="pt-2 border-t border-slate-100">
          <Button variant="secondary" className="w-full text-sm" onClick={() => onLoadAttribution(campaign.id)}>
            View attribution
          </Button>
          {attribution && (
            <div className="mt-2">
              <KeyValueTable data={attribution} embedded emptyTitle="No attribution data" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, className = '' }: { label: string; value: string | number; className?: string }) {
  return (
    <div className={className}>
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-semibold text-slate-800">{value ?? '—'}</dd>
    </div>
  );
}
